const mode = import.meta.env.MODE;
let baseUrl: string;

if (mode === 'production') {
  baseUrl = 'https://askawatch.cbre.com';
  console.log('ChangeValidation: PRODUCTION mode');
} else if (mode === 'staging') {
  baseUrl = 'https://askawatch-test.cbre.com';
  console.log('ChangeValidation: STAGING mode');
} else {
  baseUrl = 'http://localhost:8000';
  console.log('ChangeValidation: DEVELOPMENT mode');
}

// Raw API shape
interface FetchChangesApiItem {
  number: string;
  configuration_class: string;
  planned_start_date: string;
  planned_end_date: string;
  short_description?: string;
  assignment_group?: string;
  assigned_to?: string;
  description?: string;
  dd_enabled?: boolean;
}

interface FetchChangesResponse {
  success: boolean;
  changes: FetchChangesApiItem[];
  count: number;
}

interface RunValidationApiResponse {
  success: boolean;
  change_number: string;
  report_url: string[] | null; // now a list
}

interface RunAsyncResponse {
  task_id: string;
  status: string;
  change_number: string;
}

interface TaskStatusResponse {
  task_id: string;
  status: 'pending' | 'running' | 'finished' | 'error';
  change_number: string;
  report_url: string[] | null;   // now a list
  text: string | null;
  error: string | null;
}

export interface PreviousRunResult {
  change_number: string;
  report_url: string;
  configuration_class: string;
  updated_at: string;
}

export interface FetchPreviousRunsResponse {
  success: boolean;
  results: PreviousRunResult[];
  count: number;
}

// Normalized UI shape
export interface ChangeItem {
  id: string;
  title: string;
  configurationClass: string;
  plannedStart: string;
  plannedEnd: string;
  shortDescription?: string;
  assignmentGroup?: string;
  assignedTo?: string;
  description?: string;
  dd_enabled?: boolean;
}

export interface RunChangeValidationResult {
  success: boolean;
  changeNumber: string;
  reportUrls: string[]; // normalized list
}

function toIso(v: string): string {
  // If backend sends like "2025-11-20 13:40:00", normalize to ISO
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(v)) {
    return v.replace(' ', 'T') + 'Z';
  }
  return v;
}

export async function fetchChanges(): Promise<ChangeItem[]> {
  const res = await fetch(`${baseUrl}/api/changevalidation/fetch_changes`, {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });
  if (!res.ok) throw new Error(`Failed to fetch changes (${res.status})`);

  const json: FetchChangesResponse = await res.json();
  if (!json.success) throw new Error('API returned unsuccessful response');

  return (json.changes || []).map(c => ({
    id: c.number,
    title: c.configuration_class || c.number,
    configurationClass: c.configuration_class,
    plannedStart: toIso(c.planned_start_date),
    plannedEnd: toIso(c.planned_end_date),
    shortDescription: c.short_description,
    assignmentGroup: c.assignment_group,
    assignedTo: c.assigned_to,
    description: c.description,
    dd_enabled: c.dd_enabled
  }));
}

/**
 * Trigger backend validation job for a change.
 */
export async function runChangeValidation(changeNumber: string): Promise<RunChangeValidationResult> {
  const res = await fetch(`${baseUrl}/api/changevalidation/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ change_number: changeNumber })
  });
  if (!res.ok) {
    throw new Error(`Failed to start validation (${res.status})`);
  }
  const json: RunValidationApiResponse = await res.json();
  return {
    success: json.success,
    changeNumber: json.change_number,
    reportUrls: json.report_url || []
  };
}

export async function startChangeValidationAsync(changeNumber: string, userEmail: string): Promise<RunAsyncResponse> {
  const res = await fetch(`${baseUrl}/api/changevalidation/run_async`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ change_number: changeNumber, user_email: userEmail })
  });
  if (!res.ok) throw new Error(`Failed to start async validation (${res.status})`);
  return res.json();
}

export async function fetchChangeValidationStatus(taskId: string): Promise<TaskStatusResponse> {
  const res = await fetch(`${baseUrl}/api/changevalidation/status/${taskId}`, {
    method: 'GET',
    headers: { Accept: 'application/json' }
  });
  if (!res.ok) throw new Error(`Failed to get status (${res.status})`);
  return res.json();
}

/**
 * Fetch previous validation runs for a change number.
 */
export async function fetchPreviousRuns(changeNumber: string): Promise<PreviousRunResult[]> {
  const res = await fetch(
    `${baseUrl}/api/changevalidation/fetch_previous_runs?change_number=${encodeURIComponent(changeNumber)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' }
    }
  );
  if (!res.ok) throw new Error(`Failed to fetch previous runs (${res.status})`);
  const json: FetchPreviousRunsResponse = await res.json();
  if (!json.success) throw new Error('API returned unsuccessful response');
  console.log('Fetched previous runs:', json);
  return json.results || [];
}

export async function fetchPreviousHeathChecks(configuration_class: string): Promise<PreviousRunResult[]> {
    const res = await fetch(
      `${baseUrl}/api/changevalidation/fetch_previous_health_checks?configuration_class=${encodeURIComponent(configuration_class)}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' }
      }
    );
    if (!res.ok) throw new Error(`Failed to fetch previous runs (${res.status})`);
    const json: FetchPreviousRunsResponse = await res.json();
    if (!json.success) throw new Error('API returned unsuccessful response');
    console.log('Fetched previous runs:', json);
    return json.results || [];
  }

// Optional helper: start + poll until finished/error
export async function runAsyncAndWaitForReport(
  changeNumber: string,
  { intervalMs = 60_000, timeoutMs = 15 * 60_000, signal }: { intervalMs?: number; timeoutMs?: number; signal?: AbortSignal } = {}
): Promise<{ reportUrls: string[]; text: string | null }> {
  const startResp = await startChangeValidationAsync(changeNumber);
  const taskId = startResp.task_id;
  const start = performance.now();
  while (true) {
    if (signal?.aborted) throw new Error('Validation aborted');
    const status = await fetchChangeValidationStatus(taskId);
    if (status.status === 'error') throw new Error(status.error || 'Validation failed');
    if (status.status === 'finished') {
      const urls = status.report_url || [];
      if (urls.length === 0 && !status.text) throw new Error('Finished without report URLs or text');
      return { reportUrls: urls, text: status.text };
    }
    if (performance.now() - start > timeoutMs) throw new Error('Validation timeout exceeded');
    await new Promise(r => setTimeout(r, intervalMs));
  }
}